import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MasterCropData, MasterUPCData, MasterDesignData, SectionTarget } from '../types';
import {
  resolveBarcodeRowAction,
  resolveDesignTargetBarcode,
  resolveDraftDesignVerifiedBarcode,
} from '../utils/masterBarcodeRows';

// ── Shared style ──

export function actionBtnStyle(color: string): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px',
    background: 'transparent', border: `1px solid ${color}`, borderRadius: 'var(--radius-sm)',
    color, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)',
  };
}

function applyCropBarcode(crop: MasterCropData, barcode?: string): void {
  if (!barcode) return;
  crop.barcode = barcode;
}

// ── Types ──

export interface PendingChanges {
  merges: { sourceDesignId: string; targetDesignId: string }[];
  moves: { cropId: string; targetDesignId: string; targetItemNumber: string; targetSection: SectionTarget['type'] }[];
  deactivations: string[];
  reactivations: string[];
  newDesigns: { id: string; cropIds: string[]; itemNumber: string }[];
  representativeChanges: { designId: string; cropId: string }[];
}

export function emptyChanges(): PendingChanges {
  return { merges: [], moves: [], deactivations: [], reactivations: [], newDesigns: [], representativeChanges: [] };
}

export interface UndoEntry {
  label: string;
  upcData: MasterUPCData[];
  changes: PendingChanges;
  extra?: unknown;
}

export type ActionMode = 'idle' | 'crops-selected' | 'merge-source';

const DESIGN_COLORS = ['#f87171', '#60a5fa', '#34d399', '#c084fc', '#fbbf24', '#f472b6', '#2dd4bf', '#fb923c'];

export interface UseUPCActionsOptions {
  initialUpcData: MasterUPCData[];
  notify: (text: string, type?: 'success' | 'warning' | 'error') => void;
  /** Called when changes update (for syncing to parent) */
  onChangesUpdate?: (changes: PendingChanges) => void;
  /** Extra data to save/restore in undo entries */
  saveUndoExtra?: () => unknown;
  restoreUndoExtra?: (extra: unknown) => void;
}

export function useUPCActions({
  initialUpcData,
  notify,
  onChangesUpdate,
  saveUndoExtra,
  restoreUndoExtra,
}: UseUPCActionsOptions) {
  const [upcData, setUpcData] = useState<MasterUPCData[]>(() => structuredClone(initialUpcData));
  const [selectedCropIds, setSelectedCropIds] = useState<Set<string>>(new Set());
  const [mergeSourceDesignId, setMergeSourceDesignId] = useState<string | null>(null);
  const [activeDesignId, setActiveDesignId] = useState<string | null>(null);
  const [changes, setChanges] = useState<PendingChanges>(emptyChanges);
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);

  const [recentlyMovedCropIds, setRecentlyMovedCropIds] = useState<Set<string>>(new Set());
  const movedTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const markMoved = useCallback((cropIds: string[]) => {
    if (movedTimerRef.current) clearTimeout(movedTimerRef.current);
    setRecentlyMovedCropIds(new Set(cropIds));
    movedTimerRef.current = setTimeout(() => setRecentlyMovedCropIds(new Set()), 2000);
  }, []);

  const mode: ActionMode = selectedCropIds.size > 0
    ? 'crops-selected'
    : mergeSourceDesignId
      ? 'merge-source'
      : 'idle';

  // Clear active design when leaving idle mode
  useEffect(() => { if (mode !== 'idle') setActiveDesignId(null); }, [mode]);

  // Sync changes to parent
  useEffect(() => { onChangesUpdate?.(changes); }, [changes, onChangesUpdate]);

  // ── Design color map ──

  const designColorMap = useMemo(() => {
    const map = new Map<string, string>();
    let idx = 0;
    for (const u of upcData) {
      for (const v of u.designs) {
        if (!map.has(v.id)) {
          map.set(v.id, DESIGN_COLORS[idx % DESIGN_COLORS.length]);
          idx++;
        }
      }
    }
    return map;
  }, [upcData]);

  // ── Undo ──

  const pushUndo = useCallback((label: string) => {
    setUndoStack(prev => [...prev, {
      label,
      upcData: structuredClone(upcData),
      changes: structuredClone(changes),
      extra: saveUndoExtra?.(),
    }]);
  }, [upcData, changes, saveUndoExtra]);

  const undo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setUpcData(last.upcData);
      setChanges(last.changes);
      setSelectedCropIds(new Set());
      setMergeSourceDesignId(null);
      if (last.extra !== undefined) restoreUndoExtra?.(last.extra);
      notify(`Undid: ${last.label}`, 'warning');
      return prev.slice(0, -1);
    });
  }, [notify, restoreUndoExtra]);

  // ── Helpers ──

  const findCropLocation = useCallback((cropId: string, data: MasterUPCData[]): { upcIdx: number; section: 'design' | 'no-design' | 'non-front' | 'carton' | 'inactive'; designIdx?: number; cropIdx: number } | null => {
    for (let ui = 0; ui < data.length; ui++) {
      const u = data[ui];
      for (let vi = 0; vi < u.designs.length; vi++) {
        const ci = u.designs[vi].crops.findIndex(c => c.id === cropId);
        if (ci !== -1) return { upcIdx: ui, section: 'design', designIdx: vi, cropIdx: ci };
      }
      if (u.pendingCrops) {
        const pi = u.pendingCrops.findIndex(c => c.id === cropId);
        if (pi !== -1) return { upcIdx: ui, section: 'no-design', cropIdx: pi };
      }
      const uci = u.unassignedCrops.findIndex(c => c.id === cropId);
      if (uci !== -1) return { upcIdx: ui, section: 'no-design', cropIdx: uci };
      const nfi = u.nonFrontCrops.findIndex(c => c.id === cropId);
      if (nfi !== -1) return { upcIdx: ui, section: 'non-front', cropIdx: nfi };
      const cti = u.cartonCrops.findIndex(c => c.id === cropId);
      if (cti !== -1) return { upcIdx: ui, section: 'carton', cropIdx: cti };
      if (u.inactiveCrops) {
        const ii = u.inactiveCrops.findIndex(c => c.id === cropId);
        if (ii !== -1) return { upcIdx: ui, section: 'inactive', cropIdx: ii };
      }
    }
    return null;
  }, []);

  /** Check if a crop needs server-side reactivation (inactive or pending crops are !isActive on the server) */
  const needsReactivation = useCallback((cropId: string, data: MasterUPCData[]): boolean => {
    const loc = findCropLocation(cropId, data);
    if (!loc) return false;
    if (loc.section === 'inactive') return true;
    // Pending crops are isActive=false on server
    const u = data[loc.upcIdx];
    if (u.pendingCrops) {
      const crop = u.pendingCrops.find(c => c.id === cropId);
      if (crop?.isPending) return true;
    }
    return false;
  }, [findCropLocation]);

  // ── Helpers: safe crop removal (always finds by ID, avoids stale-index bugs) ──

  /** Remove a crop by ID from any section in the data array. Returns the removed crop or null. */
  const removeCropById = (cropId: string, data: MasterUPCData[]): MasterCropData | null => {
    for (const u of data) {
      for (const v of u.designs) {
        const ci = v.crops.findIndex(c => c.id === cropId);
        if (ci !== -1) return v.crops.splice(ci, 1)[0];
      }
      if (u.pendingCrops) {
        const pi = u.pendingCrops.findIndex(c => c.id === cropId);
        if (pi !== -1) return u.pendingCrops.splice(pi, 1)[0];
      }
      const ui = u.unassignedCrops.findIndex(c => c.id === cropId);
      if (ui !== -1) return u.unassignedCrops.splice(ui, 1)[0];
      const nfi = u.nonFrontCrops.findIndex(c => c.id === cropId);
      if (nfi !== -1) return u.nonFrontCrops.splice(nfi, 1)[0];
      const cti = u.cartonCrops.findIndex(c => c.id === cropId);
      if (cti !== -1) return u.cartonCrops.splice(cti, 1)[0];
      if (u.inactiveCrops) {
        const ii = u.inactiveCrops.findIndex(c => c.id === cropId);
        if (ii !== -1) {
          const crop = u.inactiveCrops.splice(ii, 1)[0];
          crop.isInactive = false;
          return crop;
        }
      }
    }
    return null;
  };

  // ── Actions ──

  const executeMove = useCallback((cropIds: Set<string>, target: SectionTarget) => {
    pushUndo(`Move ${cropIds.size} crop(s)`);

    const sourceDesignIds = new Set<string>();
    const reactivatedIds: string[] = [];
    for (const cropId of cropIds) {
      const loc = findCropLocation(cropId, upcData);
      if (loc?.section === 'design' && loc.designIdx !== undefined) {
        sourceDesignIds.add(upcData[loc.upcIdx].designs[loc.designIdx].id);
      }
      if (needsReactivation(cropId, upcData)) reactivatedIds.push(cropId);
    }

    setUpcData(prev => {
      const next: MasterUPCData[] = structuredClone(prev);
      const cropsToMove: MasterCropData[] = [];

      for (const cropId of cropIds) {
        const crop = removeCropById(cropId, next);
        if (crop) cropsToMove.push(crop);
      }

      const emptiedDesignIds: string[] = [];
      for (const u of next) {
        for (const v of u.designs) {
          if (v.crops.length === 0 && sourceDesignIds.has(v.id)) {
            emptiedDesignIds.push(v.id);
          }
        }
        u.designs = u.designs.filter(v => v.crops.length > 0);
      }

      const targetItemData = next.find((u) => u.itemNumber === target.itemNumber);
      if (!targetItemData) return prev;

      if (target.type === 'design' && target.designId) {
        const v = targetItemData.designs.find(v => v.id === target.designId);
        for (const crop of cropsToMove) {
          crop.isInactive = false;
          crop.isPending = false;
          crop.isMismatch = false;
          applyCropBarcode(crop, v ? resolveDesignTargetBarcode(v, targetItemData) : undefined);
        }
        if (v) v.crops.push(...cropsToMove);
      } else if (target.type === 'no-design') {
        for (const crop of cropsToMove) {
          crop.isInactive = false;
          crop.isPending = false;
          crop.isMismatch = false;
        }
        targetItemData.unassignedCrops.push(...cropsToMove);
      } else if (target.type === 'non-front') {
        for (const crop of cropsToMove) {
          crop.isInactive = false;
          crop.isPending = false;
          crop.isMismatch = false;
        }
        targetItemData.nonFrontCrops.push(...cropsToMove);
      } else if (target.type === 'carton') {
        for (const crop of cropsToMove) {
          crop.isInactive = false;
          crop.isPending = false;
          crop.isMismatch = false;
        }
        targetItemData.cartonCrops.push(...cropsToMove);
      } else if (target.type === 'inactive') {
        for (const crop of cropsToMove) {
          crop.isInactive = true;
          crop.isPending = false;
          crop.isMismatch = false;
        }
        if (!targetItemData.inactiveCrops) targetItemData.inactiveCrops = [];
        targetItemData.inactiveCrops.push(...cropsToMove);
      }

      if (target.type === 'design' && target.designId && emptiedDesignIds.length > 0) {
        setChanges(ch => ({
          ...ch,
          merges: [...ch.merges, ...emptiedDesignIds.map(id => ({ sourceDesignId: id, targetDesignId: target.designId! }))],
        }));
        for (const id of emptiedDesignIds) {
          notify(`Auto-merged ${id.slice(0, 8)} → ${target.designId.slice(0, 8)}`);
        }
      }

      return next;
    });
    setChanges(prev => ({
      ...prev,
      moves: [...prev.moves, ...[...cropIds].map(cropId => ({
        cropId, targetDesignId: target.designId || '', targetItemNumber: target.itemNumber, targetSection: target.type,
      }))],
      reactivations: reactivatedIds.length > 0 ? [...prev.reactivations, ...reactivatedIds] : prev.reactivations,
    }));
    setSelectedCropIds(new Set());
    markMoved([...cropIds]);
    notify(`Moved ${cropIds.size} crop${cropIds.size > 1 ? 's' : ''}`);
  }, [findCropLocation, needsReactivation, upcData, notify, pushUndo, markMoved]);

  const executeMerge = useCallback((sourceId: string, targetId: string) => {
    pushUndo(`Merge ${sourceId.slice(0, 8)} → ${targetId.slice(0, 8)}`);
    // Collect source crop IDs before mutating state
    const sourceCropIds: string[] = [];
    for (const u of upcData) {
      const v = u.designs.find(v => v.id === sourceId);
      if (v) { sourceCropIds.push(...v.crops.map(c => c.id)); break; }
    }
    setUpcData(prev => {
      const next: MasterUPCData[] = structuredClone(prev);
      let sourceDesign: MasterDesignData | null = null;
      let sourceUpcIdx = -1;
      let sourceDesignIdx = -1;

      for (let ui = 0; ui < next.length; ui++) {
        const vi = next[ui].designs.findIndex(v => v.id === sourceId);
        if (vi !== -1) {
          sourceDesign = next[ui].designs[vi];
          sourceUpcIdx = ui;
          sourceDesignIdx = vi;
          break;
        }
      }
      if (!sourceDesign) return prev;

      let targetDesign: MasterDesignData | null = null;
      for (const u of next) {
        const v = u.designs.find(v => v.id === targetId);
        if (v) { targetDesign = v; break; }
      }
      if (!targetDesign) return prev;

      targetDesign.crops.push(...sourceDesign.crops);
      next[sourceUpcIdx].designs.splice(sourceDesignIdx, 1);
      return next;
    });
    setChanges(prev => ({ ...prev, merges: [...prev.merges, { sourceDesignId: sourceId, targetDesignId: targetId }] }));
    setMergeSourceDesignId(null);
    markMoved(sourceCropIds);
    notify(`Merged ${sourceId.slice(0, 8)} → ${targetId.slice(0, 8)}`);
  }, [upcData, notify, pushUndo, markMoved]);

  const deactivateCrops = useCallback(() => {
    const ids = [...selectedCropIds];
    const idSet = new Set(ids);
    pushUndo(`Deactivate ${ids.length} crop(s)`);
    setUpcData(prev => {
      const next: MasterUPCData[] = structuredClone(prev);
      for (const u of next) {
        const deactivated: MasterCropData[] = [];
        for (const v of u.designs) {
          const removed = v.crops.filter(c => idSet.has(c.id));
          v.crops = v.crops.filter(c => !idSet.has(c.id));
          deactivated.push(...removed);
        }
        u.designs = u.designs.filter(v => v.crops.length > 0);
        if (u.pendingCrops) {
          deactivated.push(...u.pendingCrops.filter(c => idSet.has(c.id)));
          u.pendingCrops = u.pendingCrops.filter(c => !idSet.has(c.id));
        }
        deactivated.push(...u.unassignedCrops.filter(c => idSet.has(c.id)));
        u.unassignedCrops = u.unassignedCrops.filter(c => !idSet.has(c.id));
        deactivated.push(...u.nonFrontCrops.filter(c => idSet.has(c.id)));
        u.nonFrontCrops = u.nonFrontCrops.filter(c => !idSet.has(c.id));
        deactivated.push(...u.cartonCrops.filter(c => idSet.has(c.id)));
        u.cartonCrops = u.cartonCrops.filter(c => !idSet.has(c.id));
        for (const crop of deactivated) {
          crop.isInactive = true;
          crop.isPending = false;
          crop.isMismatch = false;
        }
        if (!u.inactiveCrops) u.inactiveCrops = [];
        u.inactiveCrops.push(...deactivated);
      }
      return next;
    });
    setChanges(prev => ({ ...prev, deactivations: [...prev.deactivations, ...ids] }));
    setSelectedCropIds(new Set());
    markMoved(ids);
    notify(`Deactivated ${ids.length} crop${ids.length > 1 ? 's' : ''}`, 'warning');
  }, [selectedCropIds, notify, pushUndo, markMoved]);

  const createNewDesign = useCallback((itemNumber: string, _options?: { barcode?: string | null }) => {
    const ids = [...selectedCropIds];
    const selectedCrops = upcData.flatMap((item) => [
      ...item.designs.flatMap((design) => design.crops),
      ...(item.pendingCrops ?? []),
      ...item.unassignedCrops,
      ...item.nonFrontCrops,
      ...item.cartonCrops,
      ...(item.inactiveCrops ?? []),
    ]).filter((crop) => ids.includes(crop.id));
    const draftBarcode = resolveDraftDesignVerifiedBarcode(selectedCrops);
    if (!draftBarcode) {
      notify('New designs require at least one scan crop with a barcode', 'error');
      return;
    }
    const newId = `new-${crypto.randomUUID().slice(0, 8)}`;
    const reactivatedIds = ids.filter(cropId => needsReactivation(cropId, upcData));
    pushUndo(`Create design ${newId}`);
    setUpcData(prev => {
      const next: MasterUPCData[] = structuredClone(prev);
      const cropsToMove: MasterCropData[] = [];
      for (const cropId of ids) {
        const crop = removeCropById(cropId, next);
        if (crop) cropsToMove.push(crop);
      }
      for (const u of next) u.designs = u.designs.filter(v => v.crops.length > 0);
      const targetItem = next.find((u) => u.itemNumber === itemNumber);
      if (targetItem && cropsToMove.length > 0) {
        cropsToMove.forEach((crop) => applyCropBarcode(crop, draftBarcode));
        targetItem.designs.push({
          id: newId,
          representativeCropId: cropsToMove[0].id,
          barcodeStatus: 'unconfirmed',
          barcode: draftBarcode,
          crops: cropsToMove,
        });
      }
      return next;
    });
    setChanges(prev => ({
      ...prev,
      newDesigns: [...prev.newDesigns, { id: newId, cropIds: ids, itemNumber }],
      reactivations: reactivatedIds.length > 0 ? [...prev.reactivations, ...reactivatedIds] : prev.reactivations,
    }));
    setSelectedCropIds(new Set());
    markMoved(ids);
    notify(`Created design ${newId} for barcode`);
  }, [selectedCropIds, needsReactivation, upcData, notify, pushUndo, markMoved]);

  const setRepresentative = useCallback((designId: string, cropId: string) => {
    pushUndo(`Set rep for ${designId.slice(0, 8)}`);
    setUpcData(prev => {
      const next: MasterUPCData[] = structuredClone(prev);
      for (const u of next) {
        const v = u.designs.find(v => v.id === designId);
        if (v) { v.representativeCropId = cropId; break; }
      }
      return next;
    });
    setChanges(prev => ({ ...prev, representativeChanges: [...prev.representativeChanges, { designId, cropId }] }));
    setSelectedCropIds(new Set());
    notify(`Set representative for ${designId.slice(0, 8)}`);
  }, [notify, pushUndo]);

  // ── Section click (move / merge target) ──

  const handleSectionClick = useCallback((target: SectionTarget) => {
    if (mode === 'crops-selected') {
      // Can't move crops into pending
      if (target.type === 'pending') return;
      if (target.type === 'barcode') {
        const item = upcData.find((entry) => entry.itemNumber === target.itemNumber);
        if (!item || !target.barcode) return;
        const action = resolveBarcodeRowAction(item, target.barcode);
        if (action.kind === 'missing') return;
        if (action.kind === 'blocked') {
          notify('Choose a specific design under this barcode');
          return;
        }
        if (action.kind === 'create-design') {
          createNewDesign(target.itemNumber, { barcode: action.barcode });
          return;
        }
        target = { itemNumber: target.itemNumber, type: 'design', designId: action.designId };
      }
      // Don't move if all selected crops are already in the target section
      const allAlreadyInTarget = [...selectedCropIds].every(cropId => {
        const loc = findCropLocation(cropId, upcData);
        if (!loc) return false;
        const locItemNumber = upcData[loc.upcIdx].itemNumber;
        if (locItemNumber !== target.itemNumber) return false;
        if (target.type === 'design' && target.designId && loc.section === 'design' && loc.designIdx !== undefined) {
          return upcData[loc.upcIdx].designs[loc.designIdx].id === target.designId;
        }
        return loc.section === target.type;
      });
      if (allAlreadyInTarget) {
        setSelectedCropIds(new Set());
        return;
      }
      executeMove(new Set(selectedCropIds), target);
    } else if (mode === 'merge-source' && target.type === 'design' && target.designId) {
      if (target.designId === mergeSourceDesignId) {
        setMergeSourceDesignId(null);
      } else {
        executeMerge(mergeSourceDesignId!, target.designId);
      }
    } else if (mode === 'idle') {
      if (target.type === 'barcode') return;
      const itemEntry = upcData.find((u) => u.itemNumber === target.itemNumber);
      if (!itemEntry) return;
      let cropIds: string[] = [];
      if (target.type === 'design' && target.designId) {
        const v = itemEntry.designs.find(v => v.id === target.designId);
        if (v) cropIds = v.crops.filter(c => !c.isInactive).map(c => c.id);
      } else if (target.type === 'pending') {
        cropIds = (itemEntry.pendingCrops ?? []).map(c => c.id);
      } else if (target.type === 'no-design') {
        cropIds = itemEntry.unassignedCrops.filter(c => !c.isInactive).map(c => c.id);
      } else if (target.type === 'non-front') {
        cropIds = itemEntry.nonFrontCrops.filter(c => !c.isInactive).map(c => c.id);
      } else if (target.type === 'carton') {
        cropIds = itemEntry.cartonCrops.filter(c => !c.isInactive).map(c => c.id);
      } else if (target.type === 'inactive') {
        cropIds = (itemEntry.inactiveCrops ?? []).map(c => c.id);
      }
      if (cropIds.length > 0) setSelectedCropIds(new Set(cropIds));
    }
  }, [
    mode,
    selectedCropIds,
    mergeSourceDesignId,
    upcData,
    executeMove,
    executeMerge,
    findCropLocation,
    createNewDesign,
    notify,
  ]);

  // ── Computed values ──

  const selectedCropDesignInfo = useMemo(() => {
    if (selectedCropIds.size !== 1) return null;
    const cropId = [...selectedCropIds][0];
    for (const u of upcData) {
      for (const v of u.designs) {
        if (v.crops.some(c => c.id === cropId)) {
          return { designId: v.id, cropId, isAlreadyRep: v.representativeCropId === cropId };
        }
      }
    }
    return null;
  }, [selectedCropIds, upcData]);

  const selectedCropItemNumber = useMemo(() => {
    if (selectedCropIds.size === 0) return null;
    const cropId = [...selectedCropIds][0];
    const loc = findCropLocation(cropId, upcData);
    return loc ? upcData[loc.upcIdx].itemNumber : null;
  }, [selectedCropIds, upcData, findCropLocation]);

  const selectedCropsSourceDesign = useMemo(() => {
    if (selectedCropIds.size === 0) return null;
    let designId: string | null = null;
    for (const cropId of selectedCropIds) {
      let found = false;
      for (const u of upcData) {
        for (const v of u.designs) {
          if (v.crops.some(c => c.id === cropId)) {
            if (designId === null) designId = v.id;
            else if (designId !== v.id) return null;
            found = true;
            break;
          }
        }
        if (found) break;
      }
      if (!found) return null;
    }
    return designId;
  }, [selectedCropIds, upcData]);

  const lastClickedCropRef = useRef<string | null>(null);

  // Build ordered crop list for shift-select range (scan first, then ID to match display order)
  const getGroupCropIds = useCallback((cropId: string): string[] => {
    const sortedIds = (crops: MasterCropData[]) => [...crops].sort((a, b) => {
      const aScan = a.source === 'scan' ? 0 : 1;
      const bScan = b.source === 'scan' ? 0 : 1;
      if (aScan !== bScan) return aScan - bScan;
      return a.id.localeCompare(b.id);
    }).map(c => c.id);
    for (const u of upcData) {
      for (const v of u.designs) {
        if (v.crops.some(c => c.id === cropId)) return sortedIds(v.crops);
      }
      if (u.pendingCrops?.some(c => c.id === cropId)) return sortedIds(u.pendingCrops);
      if (u.unassignedCrops.some(c => c.id === cropId)) return sortedIds(u.unassignedCrops);
      if (u.nonFrontCrops.some(c => c.id === cropId)) return sortedIds(u.nonFrontCrops);
      if (u.cartonCrops.some(c => c.id === cropId)) return sortedIds(u.cartonCrops);
      if (u.inactiveCrops?.some(c => c.id === cropId)) return sortedIds(u.inactiveCrops);
    }
    return [];
  }, [upcData]);

  const toggleCropSelection = useCallback((cropId: string, shiftKey = false) => {
    if (shiftKey && lastClickedCropRef.current && lastClickedCropRef.current !== cropId) {
      const group = getGroupCropIds(cropId);
      const lastIdx = group.indexOf(lastClickedCropRef.current);
      const curIdx = group.indexOf(cropId);
      if (lastIdx !== -1 && curIdx !== -1) {
        const start = Math.min(lastIdx, curIdx);
        const end = Math.max(lastIdx, curIdx);
        const range = group.slice(start, end + 1);
        setSelectedCropIds(prev => {
          const n = new Set(prev);
          for (const id of range) n.add(id);
          return n;
        });
        lastClickedCropRef.current = cropId;
        return;
      }
    }
    lastClickedCropRef.current = cropId;
    setSelectedCropIds(prev => {
      const n = new Set(prev);
      if (n.has(cropId)) n.delete(cropId);
      else n.add(cropId);
      return n;
    });
  }, [getGroupCropIds]);

  const resetAfterSave = useCallback((freshData: MasterUPCData[]) => {
    setUpcData(freshData);
    setChanges(emptyChanges());
    setUndoStack([]);
    setSelectedCropIds(new Set());
    setMergeSourceDesignId(null);
  }, []);

  return {
    upcData,
    setUpcData,
    selectedCropIds,
    setSelectedCropIds,
    toggleCropSelection,
    mergeSourceDesignId,
    setMergeSourceDesignId,
    activeDesignId,
    setActiveDesignId,
    mode,
    changes,
    setChanges,
    designColorMap,
    executeMove,
    executeMerge,
    deactivateCrops,
    createNewDesign,
    setRepresentative,
    handleSectionClick,
    findCropLocation,
    undoStack,
    undo,
    pushUndo,
    selectedCropDesignInfo,
    selectedCropItemNumber,
    selectedCropsSourceDesign,
    resetAfterSave,
    recentlyMovedCropIds,
  };
}
